/**
 * The desktop (`lg`, 1024px+) frame of the profile page, shared by the real
 * view and both loading skeletons so they cannot drift apart.
 *
 * Below `lg` every class here is inert and the page is the phone column it
 * always was. From `lg` it becomes two columns inside a `max-w-6xl` frame
 * aligned with the top bar: a sticky side column carrying the hero photo and
 * the "who is this" summary (name, counts, bio), and the tabs plus their
 * content on the right. Only applied in page mode — the profile rendered
 * inside a drawer keeps its single column at every width.
 */
export const PROFILE_DESKTOP = {
  /** Top bar row, widened to the page frame. */
  barInner: "wide:max-w-6xl wide:px-8",
  /**
   * iPhone Duo's system column (`side-nav:`, globals.css): the top bar
   * becomes a stack of its buttons at the top of that column, above the tab
   * rail (so never full height, or it would cover the rail), and its title
   * leaves the screen — the name is under the photo already.
   */
  barColumn:
    "status-corner:min-h-(--status-corner-height) status-corner:pt-[calc(var(--status-corner-height)*0.5_-_1.5rem)] status-corner-left:pl-(--status-corner-width) status-corner-right:pr-(--status-corner-width) side-nav:w-(--side-nav-width) side-nav:border-0 side-nav:bg-transparent side-nav:pt-[max(var(--side-nav-top),0.75rem)] side-nav:backdrop-blur-none side-nav-left:right-auto side-nav-right:left-auto",
  barColumnRow:
    "side-nav:mx-0 side-nav:h-auto side-nav:max-w-none side-nav:flex-col side-nav:gap-2 side-nav:px-0",
  barTitle: "side-nav:sr-only",
  /**
   * The spacer under the fixed bar; the column takes no height. Held upright
   * the bar sits in the top band (`status-corner:`), so the spacer only
   * covers what it reaches past the top inset the shell already pads.
   */
  barSpacer:
    "side-nav:hidden status-corner:h-[max(0px,calc(max(3.75rem,var(--status-corner-height))-env(safe-area-inset-top,0px)))]",
  /**
   * Page grid: side column beside the tabbed content. Below `md` (a landscape
   * phone, iPhone Duo closed and rotated) the side column narrows so the
   * right column keeps ~300pt instead of ~170pt. `profile-split` lets the
   * fold rule in globals.css end the side column at iPhone Duo's crease.
   */
  grid: "profile-split wide:grid wide:max-w-6xl wide:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] wide:items-start wide:gap-6 wide:px-5 wide:pt-6 wide:md:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] wide:md:gap-10 wide:md:px-8 wide:xl:grid-cols-[minmax(0,23rem)_minmax(0,1fr)] wide:xl:gap-12 side-nav:wide:pt-0",
  /**
   * The sticky side column. Capped to the space between the top bar and the
   * bottom navbar (`--bottom-nav-layout-padding` is 0-ish on the no-navbar
   * shell), and scrolls on its own if a long bio is expanded, so its bottom is
   * never stranded below the fold the way an over-tall sticky element is.
   */
  side: "wide:sticky wide:top-[calc(env(safe-area-inset-top)+5rem)] wide:flex wide:max-h-[calc(100dvh-env(safe-area-inset-top)-5rem-var(--bottom-nav-layout-padding))] wide:flex-col wide:gap-5 wide:overflow-y-auto wide:overscroll-contain wide:pb-4 side-nav:wide:top-[calc(env(safe-area-inset-top)+1.5rem)] side-nav:wide:max-h-[calc(100dvh-env(safe-area-inset-top)-1.5rem-var(--bottom-nav-layout-padding))]",
  /**
   * The hero on desktop: a rounded card rather than a full-bleed square. It
   * stays square, because profile photos are cropped square at upload; a 4:5
   * frame cut a fifth off the sides of every one. The cap keeps the summary
   * under it on screen on a short laptop, and it caps the WIDTH (the photo
   * shrinks, centred), never the height alone — that cropped the square.
   */
  hero: "wide:aspect-square wide:w-full wide:max-w-[calc(100dvh-env(safe-area-inset-top)-5rem-var(--bottom-nav-layout-padding)-15rem)] wide:shrink-0 wide:self-center wide:rounded-3xl wide:border wide:border-border dark:wide:border-glass-border-10 side-nav:wide:max-w-[calc(100dvh-env(safe-area-inset-top)-1.5rem-var(--bottom-nav-layout-padding)-15rem)]",
  /**
   * Right column. `min-w-0` keeps wide rails from blowing out the grid; it is
   * a size container so two-up lists go two-up only when it is wide enough.
   */
  main: "wide:@container wide:min-w-0",
  /**
   * Sticky tabs bar inside the right column: flush with the column edges. In
   * the system column there is no top bar to stick under, so it sticks to
   * the top safe area like the side column does.
   */
  tabsBar:
    "wide:px-0 status-corner:top-[max(3.75rem,var(--status-corner-height))] side-nav:top-[calc(env(safe-area-inset-top)+0.75rem)] side-nav:wide:top-[calc(env(safe-area-inset-top)+1.5rem)]",
  /**
   * Details tab: sections span both columns, gallery photos flow two per row.
   * Hidden children (display: none) take no cell, so the pills and About that
   * moved to the side column leave no holes.
   */
  details:
    "wide:grid wide:grid-cols-2 wide:gap-x-4 wide:gap-y-8 wide:px-0 wide:pt-4",
  section: "wide:col-span-full",
  /** Two-up lists (activities, recent events) inside a spanning section. */
  twoUpList:
    "wide:grid wide:grid-cols-1 wide:gap-2 wide:space-y-0 wide:@lg:grid-cols-2",
  /** A gallery photo as one grid cell instead of a centred 32rem card. */
  photo: "wide:mx-0 wide:max-w-none wide:rounded-3xl wide:shadow-sm",
} as const;
